import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import { brochuresAPI, contentAPI } from '../../services/api';
import './ContentManager.css';

const initialTeam = {
  name: '',
  title: '',
  role: '',
  group: 'docenten',
  commissieType: '',
  bio: '',
  sortOrder: 0,
  isActive: true
};

const initialReview = {
  name: '',
  role: '',
  category: 'hbo',
  quote: '',
  sortOrder: 0,
  isActive: true
};

const initialFlyer = {
  title: '',
  description: '',
  ctaUrl: '',
  sortOrder: 0,
  isActive: true
};

const initialBrochure = {
  title: '',
  category: 'hbo',
  description: '',
  details: '',
  sortOrder: 0
};

const parseBool = (value) => String(value) === 'true';
const MAX_BROCHURE_FILE_BYTES = 15 * 1024 * 1024;
const MAX_BROCHURE_CARD_IMAGE_BYTES = 8 * 1024 * 1024;

const TEAM_GROUP_LABELS = {
  management_staff: 'Management en Staf',
  docenten: 'Docenten',
  commissies: 'Commissies'
};

const REVIEW_CATEGORY_LABELS = {
  hbo: 'HBO',
  masterclasses: 'Masterclasses',
  masteropleidingen: 'Masteropleidingen'
};

const CONTENT_TABS = [
  { key: 'management_staff', label: 'Management en Staff' },
  { key: 'docenten', label: 'Docenten' },
  { key: 'commissies', label: 'Commissies' },
  { key: 'reviews_hbo', label: 'Recensies HBO' },
  { key: 'reviews_masterclasses', label: 'Recensies Masterclasses' },
  { key: 'reviews_masteropleidingen', label: 'Recensies Masteropleidingen' },
  { key: 'flyers', label: 'Flyers' },
  { key: 'brochures', label: 'Brochures' }
];

const normalizeTeamGroup = (value) => {
  if (value === 'management_staff' || value === 'directie') return 'management_staff';
  if (value === 'docenten' || value === 'docent') return 'docenten';
  if (value === 'commissies' || value === 'commissie') return 'commissies';
  return 'docenten';
};

const normalizeReviewCategory = (value) => {
  if (value === 'hbo' || value === 'bachelor') return 'hbo';
  if (value === 'masterclasses' || value === 'masterclass') return 'masterclasses';
  if (value === 'masteropleidingen' || value === 'masteropleiding') return 'masteropleidingen';
  if (value === 'hbo_masterclasses' || value === 'hbo-masterclasses') return 'hbo';
  return 'hbo';
};

const ContentManager = () => {
  const { user } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [commissieTypes, setCommissieTypes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [brochures, setBrochures] = useState([]);

  const validTabs = CONTENT_TABS.map((t) => t.key);
  const activeTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'management_staff';
  const setActiveTab = (key) => setSearchParams({ tab: key }, { replace: true });

  const [commissieTypeFilter, setCommissieTypeFilter] = useState('all');
  const [brochureCategoryFilter, setBrochureCategoryFilter] = useState('all');

  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  const [showBrochureModal, setShowBrochureModal] = useState(false);

  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editingFlyerId, setEditingFlyerId] = useState(null);
  const [editingBrochureId, setEditingBrochureId] = useState(null);

  const [teamForm, setTeamForm] = useState(initialTeam);
  const [reviewForm, setReviewForm] = useState(initialReview);
  const [flyerForm, setFlyerForm] = useState(initialFlyer);
  const [brochureForm, setBrochureForm] = useState(initialBrochure);

  const [teamPhoto, setTeamPhoto] = useState(null);
  const [newCommissieType, setNewCommissieType] = useState('');
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [flyerImage, setFlyerImage] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [brochureCardImage, setBrochureCardImage] = useState(null);

  const canManage = useMemo(() => user?.role === 'admin' || user?.role === 'manager', [user?.role]);

  const loadContent = async () => {
    setLoading(true);
    setError('');

    try {
      const [teamResp, commissieResp, reviewResp, flyerResp, brochureResp] = await Promise.all([
        contentAPI.getTeamMembers(),
        contentAPI.getCommissieTypes(),
        contentAPI.getReviews(),
        contentAPI.getFlyers(),
        brochuresAPI.getAll()
      ]);

      setTeamMembers(teamResp.teamMembers || []);
      setCommissieTypes((commissieResp.commissieTypes || []).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl')));
      setReviews(reviewResp.reviews || []);
      setFlyers(flyerResp.flyers || []);
      const sortedBrochures = [...(brochureResp || [])].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
        const bOrder = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a?.title || '').localeCompare(String(b?.title || ''));
      });
      setBrochures(sortedBrochures);
    } catch (loadError) {
      setError(loadError.message || 'Content laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    loadContent();
  }, [canManage]);

  const resetMessages = () => {
    setError('');
    setOk('');
  };

  const isValidUploadSize = (file, maxBytes, label) => {
    if (!file) return true;
    if (file.size <= maxBytes) return true;

    const maxMb = Math.round((maxBytes / (1024 * 1024)) * 10) / 10;
    setError(`${label} is te groot. Maximum is ${maxMb} MB.`);
    return false;
  };

  const openTeamCreate = () => {
    resetMessages();
    setEditingTeamId(null);
    const defaultGroup = activeTab === 'commissies'
      ? 'commissies'
      : activeTab === 'management_staff'
        ? 'management_staff'
        : 'docenten';
    setTeamForm({ ...initialTeam, group: defaultGroup });
    setTeamPhoto(null);
    setShowTeamModal(true);
  };

  const openTeamEdit = (item) => {
    resetMessages();
    setEditingTeamId(item.id);
    setTeamForm({
      name: item.name || '',
      title: item.title || '',
      role: item.role || '',
      group: item.group || 'docenten',
      commissieType: item.commissieType || '',
      bio: item.bio || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false
    });
    setTeamPhoto(null);
    setShowTeamModal(true);
  };

  const saveTeam = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');

    try {
      if (editingTeamId) {
        await contentAPI.updateTeamMember(editingTeamId, teamForm, teamPhoto);
        setOk('Teamlid bijgewerkt');
      } else {
        await contentAPI.createTeamMember(teamForm, teamPhoto);
        setOk('Teamlid aangemaakt');
      }
      setShowTeamModal(false);
      await loadContent();
    } catch (saveError) {
      setError(saveError.message || 'Teamlid opslaan mislukt');
    }
  };

  const addCommissieType = async () => {
    const value = String(newCommissieType || '').trim();
    if (!value) return;

    setError('');
    setOk('');
    try {
      const created = await contentAPI.createCommissieType({ name: value });
      const next = [...commissieTypes, created].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl'));
      setCommissieTypes(next);
      setTeamForm((prev) => ({ ...prev, commissieType: created.name || value }));
      setNewCommissieType('');
      setOk('Commissietype toegevoegd');
    } catch (saveError) {
      setError(saveError.message || 'Commissietype toevoegen mislukt');
    }
  };

  const removeCommissieType = async (item) => {
    if (!item?.id) return;
    if (!window.confirm(`Weet je zeker dat je commissietype "${item.name}" wilt verwijderen?`)) return;

    setError('');
    setOk('');
    try {
      await contentAPI.deleteCommissieType(item.id);
      const remaining = commissieTypes.filter((existing) => existing.id !== item.id);
      setCommissieTypes(remaining);
      setTeamForm((prev) => ({
        ...prev,
        commissieType: prev.commissieType === item.name ? '' : prev.commissieType
      }));
      setOk('Commissietype verwijderd');
      await loadContent();
    } catch (deleteError) {
      setError(deleteError.message || 'Commissietype verwijderen mislukt');
    }
  };

  const removeTeam = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit teamlid wilt verwijderen?')) return;
    setError('');
    setOk('');

    try {
      await contentAPI.deleteTeamMember(id);
      setOk('Teamlid verwijderd');
      await loadContent();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  const openReviewCreate = () => {
    resetMessages();
    setEditingReviewId(null);
    setReviewForm(initialReview);
    setReviewPhoto(null);
    setShowReviewModal(true);
  };

  const openReviewEdit = (item) => {
    resetMessages();
    setEditingReviewId(item.id);
    setReviewForm({
      name: item.name || '',
      role: item.role || '',
      category: normalizeReviewCategory(item.category || 'hbo'),
      quote: item.quote || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false
    });
    setReviewPhoto(null);
    setShowReviewModal(true);
  };

  const saveReview = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');

    try {
      if (editingReviewId) {
        await contentAPI.updateReview(editingReviewId, reviewForm, reviewPhoto);
        setOk('Recensie bijgewerkt');
      } else {
        await contentAPI.createReview(reviewForm, reviewPhoto);
        setOk('Recensie toegevoegd');
      }
      setShowReviewModal(false);
      await loadContent();
    } catch (saveError) {
      setError(saveError.message || 'Recensie opslaan mislukt');
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze recensie wilt verwijderen?')) return;
    setError('');
    setOk('');

    try {
      await contentAPI.deleteReview(id);
      setOk('Recensie verwijderd');
      await loadContent();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  const openFlyerCreate = () => {
    resetMessages();
    setEditingFlyerId(null);
    setFlyerForm(initialFlyer);
    setFlyerImage(null);
    setShowFlyerModal(true);
  };

  const openFlyerEdit = (item) => {
    resetMessages();
    setEditingFlyerId(item.id);
    setFlyerForm({
      title: item.title || '',
      description: item.description || '',
      ctaUrl: item.ctaUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false
    });
    setFlyerImage(null);
    setShowFlyerModal(true);
  };

  const saveFlyer = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');

    try {
      if (editingFlyerId) {
        await contentAPI.updateFlyer(editingFlyerId, flyerForm, flyerImage);
        setOk('Flyer bijgewerkt');
      } else {
        await contentAPI.createFlyer(flyerForm, flyerImage);
        setOk('Flyer toegevoegd');
      }
      setShowFlyerModal(false);
      await loadContent();
    } catch (saveError) {
      setError(saveError.message || 'Flyer opslaan mislukt');
    }
  };

  const removeFlyer = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze flyer wilt verwijderen?')) return;
    setError('');
    setOk('');

    try {
      await contentAPI.deleteFlyer(id);
      setOk('Flyer verwijderd');
      await loadContent();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  const openBrochureCreate = () => {
    resetMessages();
    setEditingBrochureId(null);
    setBrochureForm(initialBrochure);
    setBrochureFile(null);
    setBrochureCardImage(null);
    setShowBrochureModal(true);
  };

  const openBrochureEdit = (item) => {
    resetMessages();
    setEditingBrochureId(item.id);
    setBrochureForm({
      title: item.title || '',
      category: item.category || 'hbo',
      description: item.description || '',
      details: item.details || '',
      sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0
    });
    setBrochureFile(null);
    setBrochureCardImage(null);
    setShowBrochureModal(true);
  };

  const saveBrochure = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');

    try {
      const payload = { ...brochureForm, cardImageFile: brochureCardImage };
      if (editingBrochureId) {
        await brochuresAPI.update(editingBrochureId, payload, brochureFile);
        setOk('Brochure bijgewerkt');
      } else {
        await brochuresAPI.create(payload, brochureFile);
        setOk('Brochure toegevoegd');
      }
      setShowBrochureModal(false);
      await loadContent();
    } catch (saveError) {
      setError(saveError.message || 'Brochure opslaan mislukt');
    }
  };

  const removeBrochure = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze brochure wilt verwijderen?')) return;
    setError('');
    setOk('');

    try {
      await brochuresAPI.delete(id);
      setOk('Brochure verwijderd');
      await loadContent();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  const filteredTeamMembers = teamMembers.filter((item) => normalizeTeamGroup(item.group) === activeTab);

  const commissieMembersGrouped = useMemo(() => {
    const members = teamMembers.filter((m) => normalizeTeamGroup(m.group) === 'commissies');
    const filtered = commissieTypeFilter === 'all'
      ? members
      : members.filter((m) => (m.commissieType || '') === commissieTypeFilter);
    return filtered.reduce((acc, m) => {
      const key = m.commissieType || '— Geen Type —';
      if (!acc[key]) acc[key] = [];
      acc[key].push(m);
      return acc;
    }, {});
  }, [teamMembers, commissieTypeFilter]);

  const brochureCategories = useMemo(() => {
    const cats = [...new Set(brochures.map((b) => b.category || '').filter(Boolean))].sort((a, b) => a.localeCompare(b, 'nl'));
    return cats;
  }, [brochures]);

  const filteredBrochures = useMemo(() => {
    if (brochureCategoryFilter === 'all') return brochures;
    return brochures.filter((b) => (b.category || '') === brochureCategoryFilter);
  }, [brochures, brochureCategoryFilter]);

  const filteredReviews = reviews.filter((item) => {
    const category = normalizeReviewCategory(item.category);
    if (activeTab === 'reviews_hbo') return category === 'hbo';
    if (activeTab === 'reviews_masterclasses') return category === 'masterclasses';
    if (activeTab === 'reviews_masteropleidingen') return category === 'masteropleidingen';
    return false;
  });

  if (!canManage) {
    return <div className="content-manager-page"><p>Alleen admin en manager hebben toegang.</p></div>;
  }

  return (
    <div className="content-manager-page">
      <div className="page-header">
        <h1>Website Content</h1>
      </div>

      {error && <p className="cm-message cm-error">{error}</p>}
      {ok && <p className="cm-message cm-ok">{ok}</p>}

      {loading ? (
        <p>Content laden...</p>
      ) : (
        <>
          <div className="cm-topbar" role="tablist" aria-label="Website Content segmenten">
            <NavLink
              to="/admin/blogs"
              className={({ isActive }) => `cm-topbar-btn cm-topbar-link${isActive ? ' active' : ''}`}
            >
              Nieuws beheren
            </NavLink>
            {CONTENT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`cm-topbar-btn${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => { setActiveTab(tab.key); setCommissieTypeFilter('all'); setBrochureCategoryFilter('all'); }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {['management_staff', 'docenten'].includes(activeTab) && (
            <section className="cm-section">
              <div className="cm-section-header">
                <h2>{TEAM_GROUP_LABELS[activeTab]}</h2>
                <button className="btn-primary" onClick={openTeamCreate}>Teamlid toevoegen</button>
              </div>
              <div className="cm-grid">
                {filteredTeamMembers.map((item) => (
                  <article className="cm-card" key={item.id}>
                    <div className="cm-card-title-row">
                      <h3>{item.name}</h3>
                      <span className={`cm-badge ${normalizeTeamGroup(item.group)}`}>
                        {TEAM_GROUP_LABELS[normalizeTeamGroup(item.group)]}
                      </span>
                    </div>
                    <p>{item.title || '-'}</p>
                    <p className="cm-muted">{item.role || '-'}</p>
                    {item.photo?.filename && (
                      <img src={contentAPI.teamMemberPhotoUrl(item.id)} alt={item.name} className="cm-thumb" />
                    )}
                    <div className="cm-actions">
                      <button className="btn-secondary" onClick={() => openTeamEdit(item)}>Bewerken</button>
                      <button className="btn-danger" onClick={() => removeTeam(item.id)}>Verwijderen</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'commissies' && (
            <section className="cm-section">
              <div className="cm-section-header">
                <h2>Commissies</h2>
                <div className="cm-commissie-filter">
                  <label className="cm-commissie-filter-label">Type:</label>
                  <select
                    className="form-input cm-commissie-filter-select"
                    value={commissieTypeFilter}
                    onChange={(e) => setCommissieTypeFilter(e.target.value)}
                  >
                    <option value="all">Alle types</option>
                    {commissieTypes.map((ct) => (
                      <option key={ct.id} value={ct.name}>{ct.name}</option>
                    ))}
                  </select>
                </div>
                <button className="btn-primary" onClick={openTeamCreate}>Commissielid toevoegen</button>
              </div>
              {Object.keys(commissieMembersGrouped).length === 0 && (
                <p className="cm-muted">Geen commissieleden gevonden.</p>
              )}
              {Object.entries(commissieMembersGrouped)
                .sort(([a], [b]) => a.localeCompare(b, 'nl'))
                .map(([type, members]) => (
                  <div key={type} className="cm-commissie-group">
                    <h3 className="cm-commissie-group-title">{type}</h3>
                    <div className="cm-grid">
                      {members.map((item) => (
                        <article className="cm-card" key={item.id}>
                          <div className="cm-card-title-row">
                            <h3>{item.name}</h3>
                            <span className="cm-badge commissies">Commissies</span>
                          </div>
                          <p>{item.title || '-'}</p>
                          <p className="cm-muted">{item.role || '-'}</p>
                          {item.photo?.filename && (
                            <img src={contentAPI.teamMemberPhotoUrl(item.id)} alt={item.name} className="cm-thumb" />
                          )}
                          <div className="cm-actions">
                            <button className="btn-secondary" onClick={() => openTeamEdit(item)}>Bewerken</button>
                            <button className="btn-danger" onClick={() => removeTeam(item.id)}>Verwijderen</button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                ))}
            </section>
          )}

          {['reviews_hbo', 'reviews_masterclasses', 'reviews_masteropleidingen'].includes(activeTab) && (
            <section className="cm-section">
              <div className="cm-section-header">
                <h2>
                  {activeTab === 'reviews_hbo'
                    ? 'Recensies HBO'
                    : activeTab === 'reviews_masterclasses'
                      ? 'Recensies Masterclasses'
                      : 'Recensies Masteropleidingen'}
                </h2>
                <button className="btn-primary" onClick={openReviewCreate}>Recensie toevoegen</button>
              </div>
              <div className="cm-grid">
                {filteredReviews.map((item) => (
                  <article className="cm-card" key={item.id}>
                    <div className="cm-card-title-row">
                      <h3>{item.name}</h3>
                      <span className={`cm-badge review-${normalizeReviewCategory(item.category)}`}>
                        {REVIEW_CATEGORY_LABELS[normalizeReviewCategory(item.category)]}
                      </span>
                    </div>
                    <p className="cm-muted">{item.role || '-'}</p>
                    <p className="cm-quote">"{item.quote}"</p>
                    {item.photo?.filename && (
                      <img src={contentAPI.reviewPhotoUrl(item.id)} alt={item.name} className="cm-thumb" />
                    )}
                    <div className="cm-actions">
                      <button className="btn-secondary" onClick={() => openReviewEdit(item)}>Bewerken</button>
                      <button className="btn-danger" onClick={() => removeReview(item.id)}>Verwijderen</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'flyers' && (
            <section className="cm-section">
              <div className="cm-section-header">
                <h2>Flyers</h2>
                <button className="btn-primary" onClick={openFlyerCreate}>Flyer toevoegen</button>
              </div>
              <div className="cm-grid">
                {flyers.map((item) => (
                  <article className="cm-card" key={item.id}>
                    <h3>{item.title}</h3>
                    <p>{item.description || '-'}</p>
                    {item.image?.filename && (
                      <img src={contentAPI.flyerImageUrl(item.id)} alt={item.title} className="cm-flyer-image" />
                    )}
                    <p className="cm-muted">Link: {item.ctaUrl || '-'}</p>
                    <div className="cm-actions">
                      <button className="btn-secondary" onClick={() => openFlyerEdit(item)}>Bewerken</button>
                      <button className="btn-danger" onClick={() => removeFlyer(item.id)}>Verwijderen</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === 'brochures' && (
            <section className="cm-section">
              <div className="cm-section-header">
                <h2>Brochures</h2>
                <div className="cm-commissie-filter">
                  <label className="cm-commissie-filter-label">Categorie:</label>
                  <select
                    className="form-input cm-commissie-filter-select"
                    value={brochureCategoryFilter}
                    onChange={(e) => setBrochureCategoryFilter(e.target.value)}
                  >
                    <option value="all">Alle categorieën</option>
                    {brochureCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <button className="btn-primary" onClick={openBrochureCreate}>Nieuwe Brochure Toevoegen</button>
              </div>
              <div className="cm-grid">
                {filteredBrochures.map((item) => (
                  <article className="cm-card" key={item.id}>
                    <h3>{item.title}</h3>
                    <p className="cm-muted">Categorie: {item.category || '-'}</p>
                    <p>{item.description || '-'}</p>
                    <p className="cm-muted">Volgorde: {Number.isFinite(Number(item.sortOrder)) ? item.sortOrder : 0}</p>
                    {item.hasCardImage && (
                      <img src={brochuresAPI.cardImageUrl(item.id)} alt={item.title} className="cm-flyer-image" />
                    )}
                    <div className="cm-actions">
                      <a className="btn-secondary" href={brochuresAPI.viewUrl(item.id)} target="_blank" rel="noreferrer">Bekijken</a>
                      <a className="btn-secondary" href={brochuresAPI.downloadUrl(item.id)} target="_blank" rel="noreferrer">Download</a>
                    </div>
                    <div className="cm-actions">
                      <button className="btn-secondary" onClick={() => openBrochureEdit(item)}>Bewerken</button>
                      <button className="btn-danger" onClick={() => removeBrochure(item.id)}>Verwijderen</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Modal
        isOpen={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        title={editingTeamId ? 'Teamlid bewerken' : 'Teamlid toevoegen'}
      >
        <form className="cm-form" onSubmit={saveTeam}>
          <input
            className="form-input"
            placeholder="Naam"
            value={teamForm.name}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <input
            className="form-input"
            placeholder="Functietitel"
            value={teamForm.title}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, title: e.target.value }))}
          />
          <input
            className="form-input"
            placeholder="Rol"
            value={teamForm.role}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, role: e.target.value }))}
          />
          <select
            className="form-input"
            value={teamForm.group}
            onChange={(e) => setTeamForm((prev) => ({
              ...prev,
              group: e.target.value,
              commissieType: e.target.value === 'commissies' ? prev.commissieType : ''
            }))}
          >
            <option value="management_staff">Management en Staf</option>
            <option value="docenten">Docenten</option>
            <option value="commissies">Commissies</option>
          </select>
          {teamForm.group === 'commissies' && (
            <>
              <select
                className="form-input"
                value={teamForm.commissieType}
                onChange={(e) => setTeamForm((prev) => ({ ...prev, commissieType: e.target.value }))}
              >
                <option value="">Selecteer commissietype</option>
                {commissieTypes.map((item) => (
                  <option key={item.id} value={item.name}>{item.name}</option>
                ))}
              </select>

              <div className="cm-inline-row">
                <input
                  className="form-input"
                  placeholder="Nieuw commissietype (bijv. Examen Commissie)"
                  value={newCommissieType}
                  onChange={(e) => setNewCommissieType(e.target.value)}
                />
                <button type="button" className="btn-secondary" onClick={addCommissieType}>Toevoegen</button>
              </div>

              {commissieTypes.length > 0 && (
                <div className="cm-chip-list">
                  {commissieTypes.map((item) => (
                    <span key={item.id} className="cm-chip">
                      {item.name}
                      <button type="button" className="cm-chip-delete" onClick={() => removeCommissieType(item)}>x</button>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
          <textarea
            className="form-textarea"
            placeholder="Korte bio"
            value={teamForm.bio}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, bio: e.target.value }))}
          />
          <input
            className="form-input"
            type="number"
            placeholder="Volgorde"
            value={teamForm.sortOrder}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
          />
          <select
            className="form-input"
            value={String(teamForm.isActive)}
            onChange={(e) => setTeamForm((prev) => ({ ...prev, isActive: parseBool(e.target.value) }))}
          >
            <option value="true">Actief</option>
            <option value="false">Inactief</option>
          </select>
          <input
            className="form-input"
            type="file"
            accept="image/*"
            onChange={(e) => setTeamPhoto(e.target.files?.[0] || null)}
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary">Opslaan</button>
            <button type="button" className="btn-secondary" onClick={() => setShowTeamModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        title={editingReviewId ? 'Recensie bewerken' : 'Recensie toevoegen'}
      >
        <form className="cm-form" onSubmit={saveReview}>
          <input
            className="form-input"
            placeholder="Naam"
            value={reviewForm.name}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <input
            className="form-input"
            placeholder="Rol"
            value={reviewForm.role}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, role: e.target.value }))}
          />
          <select
            className="form-input"
            value={reviewForm.category}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, category: e.target.value }))}
          >
            <option value="hbo">HBO</option>
            <option value="masterclasses">Masterclasses</option>
            <option value="masteropleidingen">Masteropleidingen</option>
          </select>
          <textarea
            className="form-textarea"
            placeholder="Recensie"
            value={reviewForm.quote}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, quote: e.target.value }))}
            required
          />
          <input
            className="form-input"
            type="number"
            placeholder="Volgorde"
            value={reviewForm.sortOrder}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
          />
          <select
            className="form-input"
            value={String(reviewForm.isActive)}
            onChange={(e) => setReviewForm((prev) => ({ ...prev, isActive: parseBool(e.target.value) }))}
          >
            <option value="true">Actief</option>
            <option value="false">Inactief</option>
          </select>
          <input
            className="form-input"
            type="file"
            accept="image/*"
            onChange={(e) => setReviewPhoto(e.target.files?.[0] || null)}
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary">Opslaan</button>
            <button type="button" className="btn-secondary" onClick={() => setShowReviewModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showFlyerModal}
        onClose={() => setShowFlyerModal(false)}
        title={editingFlyerId ? 'Flyer bewerken' : 'Flyer toevoegen'}
      >
        <form className="cm-form" onSubmit={saveFlyer}>
          <input
            className="form-input"
            placeholder="Titel"
            value={flyerForm.title}
            onChange={(e) => setFlyerForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <textarea
            className="form-textarea"
            placeholder="Beschrijving"
            value={flyerForm.description}
            onChange={(e) => setFlyerForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <input
            className="form-input"
            placeholder="Call-to-action link"
            value={flyerForm.ctaUrl}
            onChange={(e) => setFlyerForm((prev) => ({ ...prev, ctaUrl: e.target.value }))}
          />
          <input
            className="form-input"
            type="number"
            placeholder="Volgorde"
            value={flyerForm.sortOrder}
            onChange={(e) => setFlyerForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
          />
          <select
            className="form-input"
            value={String(flyerForm.isActive)}
            onChange={(e) => setFlyerForm((prev) => ({ ...prev, isActive: parseBool(e.target.value) }))}
          >
            <option value="true">Actief</option>
            <option value="false">Inactief</option>
          </select>
          <input
            className="form-input"
            type="file"
            accept="image/*"
            onChange={(e) => setFlyerImage(e.target.files?.[0] || null)}
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary">Opslaan</button>
            <button type="button" className="btn-secondary" onClick={() => setShowFlyerModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showBrochureModal}
        onClose={() => setShowBrochureModal(false)}
        title={editingBrochureId ? 'Brochure bewerken' : 'Nieuwe Brochure Toevoegen'}
      >
        <form className="cm-form" onSubmit={saveBrochure}>
          <input
            className="form-input"
            placeholder="Titel"
            value={brochureForm.title}
            onChange={(e) => setBrochureForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <select
            className="form-input"
            value={brochureForm.category}
            onChange={(e) => setBrochureForm((prev) => ({ ...prev, category: e.target.value }))}
          >
            <option value="hbo">HBO</option>
            <option value="master">Master</option>
            <option value="training">Masterclass</option>
            <option value="other">Overig</option>
          </select>
          <textarea
            className="form-textarea"
            placeholder="Korte beschrijving"
            value={brochureForm.description}
            onChange={(e) => setBrochureForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <textarea
            className="form-textarea"
            placeholder="Detailtekst"
            value={brochureForm.details}
            onChange={(e) => setBrochureForm((prev) => ({ ...prev, details: e.target.value }))}
            rows={8}
          />
          <input
            className="form-input"
            type="number"
            placeholder="Volgorde"
            value={brochureForm.sortOrder}
            onChange={(e) => setBrochureForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
          />
          <label className="cm-muted">Brochure bestand (PDF/JPG/PNG/DOC)</label>
          <input
            className="form-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={(e) => {
              const selected = e.target.files?.[0] || null;
              if (!isValidUploadSize(selected, MAX_BROCHURE_FILE_BYTES, 'Brochurebestand')) {
                e.target.value = '';
                return;
              }
              setBrochureFile(selected);
            }}
          />
          <label className="cm-muted">Banner / Card afbeelding</label>
          <input
            className="form-input"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            onChange={(e) => {
              const selected = e.target.files?.[0] || null;
              if (!isValidUploadSize(selected, MAX_BROCHURE_CARD_IMAGE_BYTES, 'Card afbeelding')) {
                e.target.value = '';
                return;
              }
              setBrochureCardImage(selected);
            }}
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary">Opslaan</button>
            <button type="button" className="btn-secondary" onClick={() => setShowBrochureModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ContentManager;
